import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { createApiResponse, createErrorApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse, ImpersonationTargetType } from "@entrilia/shared/api/types";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import PageTabs from "../../../common/PageTabs";
import InvestorPortalImpersonateButton from "../InvestorPortalImpersonateButton";
import type { PortalStyleSettings, SavedPortalStyleSettings } from "./investorPortalStyleState";
import {
  getBrandingUpdateRequest,
  getInitialPortalStyleSettings,
  getSignOnUpdateRequest,
  initFromSavedSettingsAction,
  isBrandingStateDirty,
  isSignOnStateDirty,
  updateStyleSettingAction,
} from "./investorPortalStyleState";
import PortalPagePreview from "./PortalPagePreview";
import PortalPagesSettings from "./PortalPagesSettings";
import SignInPagePreview from "./SignInPagePreview";
import SignInPageSettings from "./SignInPageSettings";

type PortalStyleTab = "portalPages" | "signInPage";

const updateFundPortalCoreBrandingSettings = withErrorHandling(adminApi.updateFundPortalCoreBrandingSettings);
const updateSignOnSettings = withErrorHandling(adminApi.updateSignOnSettings);

const fetchSettings = async (): Promise<ApiResponse<SavedPortalStyleSettings>> => {
  const [portalSettingsResp, signOnSettingsResp] = await Promise.all([
    adminApi.getFundPortalCoreBrandingSettings(),
    adminApi.getSignOnSettings(),
  ]);

  if (!portalSettingsResp.success) {
    return createErrorApiResponse(portalSettingsResp.error);
  }

  if (!signOnSettingsResp.success) {
    return createErrorApiResponse(signOnSettingsResp.error);
  }

  return createApiResponse({
    portalSettings: portalSettingsResp.data,
    signOnSettings: signOnSettingsResp.data,
  });
};

interface Props {
  onClose: () => void;
  impersonationTarget?: ImpersonationTargetType;
}

const InvestorPortalStyleDialog = ({ onClose, impersonationTarget }: Props) => {
  const { hasAnyPermission } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [settings, setSettings] = useState<PortalStyleSettings>(getInitialPortalStyleSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState<PortalStyleTab>("portalPages");

  const [savedSettings, fetchError, { setData: setSavedSettings }] = useFetch(fetchSettings, {
    onSetData: (data) => setSettings(initFromSavedSettingsAction(data)),
  });

  const hasPermissionToEdit = hasAnyPermission([
    "ManageInvestorPortalSettings",
    "ManagePortfolioMonitoringPortalSettings",
  ]);

  const isDirty =
    savedSettings !== undefined &&
    (isBrandingStateDirty(settings, savedSettings.portalSettings) ||
      isSignOnStateDirty(settings, savedSettings.signOnSettings));

  const saveBrandingSettings = async () => {
    if (!savedSettings || !isBrandingStateDirty(settings, savedSettings.portalSettings)) {
      return savedSettings?.portalSettings;
    }

    const brandingUpdateRequest = getBrandingUpdateRequest(settings, savedSettings.portalSettings);
    const [resp, error] = await updateFundPortalCoreBrandingSettings(brandingUpdateRequest);
    if (error) {
      logError(error, "[InvestorPortalStyleDialog] saveBrandingSettings");
      sendNotificationError(error.message);
      return undefined;
    }

    setSavedSettings({ ...savedSettings, portalSettings: resp });
    return resp;
  };

  const saveSignOnSettings = async () => {
    if (!savedSettings || !isSignOnStateDirty(settings, savedSettings.signOnSettings)) {
      return savedSettings?.signOnSettings;
    }

    const signOnUpdateRequest = getSignOnUpdateRequest(settings, savedSettings.signOnSettings);
    const [resp, error] = await updateSignOnSettings(signOnUpdateRequest);
    if (error) {
      logError(error, "[InvestorPortalStyleDialog] saveSignOnSettings");
      sendNotificationError(error.message);
      return undefined;
    }

    setSavedSettings({ ...savedSettings, signOnSettings: resp });
    return resp;
  };

  const handleSave = async () => {
    setIsSaving(true);
    const signOnSettings = await saveSignOnSettings();
    const portalSettings = await saveBrandingSettings();
    if (signOnSettings && portalSettings) {
      sendNotification("Settings saved successfully");
      setSettings(initFromSavedSettingsAction({ portalSettings, signOnSettings }));
    }

    setIsSaving(false);
  };

  const handleChange = (update: Partial<PortalStyleSettings>) => setSettings(updateStyleSettingAction(update));

  if (fetchError) {
    logError(fetchError, "[InvestorPortalStyleDialog] fetch");
  }

  const isPortalPagesTab = selectedTab === "portalPages";

  return (
    <Dialog open fullWidth maxWidth="xl">
      <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Portal Style</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <InvestorPortalImpersonateButton
            variant="outlined"
            color="secondary"
            params={impersonationTarget ? { target: impersonationTarget } : undefined}
          />
          <CloseIconButton onClick={onClose} />
        </Stack>
      </DialogTitle>
      <PageTabs
        tabs={["portalPages", "signInPage"]}
        labels={["Portal Pages", "Sign In Page"]}
        value={selectedTab}
        onChange={setSelectedTab}
      >
        <DialogContent sx={{ p: 0, height: "80vh" }}>
          {fetchError && <DataLoadingFailed title="Could not load style settings" />}
          {!fetchError && !savedSettings && <InlineLoader />}
          {!fetchError && savedSettings && (
            <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ height: "100%" }}>
              <Box sx={{ width: 400, flexShrink: 0, overflowY: "auto", p: 3 }}>
                {isPortalPagesTab ? (
                  <PortalPagesSettings
                    settings={settings}
                    portalSettings={savedSettings.portalSettings}
                    disabled={!hasPermissionToEdit}
                    isSaving={isSaving}
                    onChange={handleChange}
                  />
                ) : (
                  <SignInPageSettings
                    settings={settings}
                    signOnSettings={savedSettings.signOnSettings}
                    disabled={!hasPermissionToEdit}
                    isSaving={isSaving}
                    onChange={handleChange}
                  />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0, overflow: "auto", p: 3, bgcolor: "background.grey" }}>
                {isPortalPagesTab ? (
                  <PortalPagePreview settings={settings} />
                ) : (
                  <SignInPagePreview settings={settings} />
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
      </PageTabs>
      {hasPermissionToEdit && (
        <>
          <Divider />
          <DialogButtons onCancel={onClose}>
            <Button loading={isSaving} onClick={handleSave} variant="contained" disabled={!isDirty}>
              Save
            </Button>
          </DialogButtons>
        </>
      )}
    </Dialog>
  );
};

export default InvestorPortalStyleDialog;
